import type { GuideDaySchedule, GuideSchedule } from '@/types';

/**
 * Client mirror of the server's slot rules (server: modules/bookings/slot-claims.ts) so the
 * pickers only offer times the server will accept. The server still has the final say.
 *
 * A tour [start, end) fits when it sits inside working hours, starts no earlier than
 * `earliestStart` (today), lasts whole hours, and [start, end + buffer) misses every busy range.
 */

export type ScheduleMeta = Pick<
  GuideSchedule,
  'workingHours' | 'slotMinutes' | 'bufferMinutes' | 'maxFullDayDays'
>;

export function toMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export function fromMinutes(total: number): string {
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** "16:30" → "4:30 PM" */
export function formatClockTime(time: string): string {
  const total = toMinutes(time);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

export function isTourAvailable(
  day: GuideDaySchedule | undefined,
  meta: ScheduleMeta,
  start: number,
  end: number
): boolean {
  if (!day || day.status === 'past' || day.status === 'vacation' || day.status === 'full') {
    return false;
  }
  const dayStart = toMinutes(meta.workingHours.start);
  const dayEnd = toMinutes(meta.workingHours.end);
  if (start < dayStart || end > dayEnd) return false;
  if (day.earliestStart && start < toMinutes(day.earliestStart)) return false;
  if (start % meta.slotMinutes !== 0) return false;
  const length = end - start;
  if (length < 60 || length % 60 !== 0) return false;

  const occupiedUntil = end + meta.bufferMinutes;
  return day.busy.every(
    (range) => occupiedUntil <= toMinutes(range.from) || start >= toMinutes(range.to)
  );
}

/** Date-only arithmetic on "YYYY-MM-DD" strings (no timezone drift). */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function daysBetweenInclusive(from: string, to: string): number {
  return (
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
  );
}

/** Full-day trips need every day in the range completely free. */
export function isFullDayRangeBookable(
  days: Map<string, GuideDaySchedule>,
  meta: ScheduleMeta,
  from: string,
  to: string
): boolean {
  if (to < from) return false;
  if (daysBetweenInclusive(from, to) > meta.maxFullDayDays) return false;
  for (let day = from; day <= to; day = addDays(day, 1)) {
    if (days.get(day)?.status !== 'free') return false;
  }
  return true;
}

/**
 * Longest full-day trip starting on `from`: consecutive free days, capped at maxFullDayDays.
 * 0 when `from` itself is not free. Days not loaded yet count as not free.
 */
export function maxFullDayLength(
  days: Map<string, GuideDaySchedule>,
  meta: ScheduleMeta,
  from: string
): number {
  let length = 0;
  let day = from;
  while (length < meta.maxFullDayDays && days.get(day)?.status === 'free') {
    length += 1;
    day = addDays(day, 1);
  }
  return length;
}

export interface StartSlot {
  /** Minutes since midnight. */
  start: number;
  available: boolean;
}

/**
 * Start-time chips for a tour of `hours`: every slot step where the tour would still end
 * inside working hours. Past times (before `earliestStart` today) are left out entirely.
 */
export function getStartSlots(
  day: GuideDaySchedule | undefined,
  meta: ScheduleMeta,
  hours: number
): StartSlot[] {
  const slots: StartSlot[] = [];
  const length = hours * 60;
  const earliest = day?.earliestStart ? toMinutes(day.earliestStart) : 0;
  const dayEnd = toMinutes(meta.workingHours.end);
  for (let s = toMinutes(meta.workingHours.start); s + length <= dayEnd; s += meta.slotMinutes) {
    if (s < earliest) continue;
    slots.push({ start: s, available: isTourAvailable(day, meta, s, s + length) });
  }
  return slots;
}

/** Tour lengths (whole hours) the working day can hold, e.g. [1..9] for 9 AM–6 PM. */
export function getDurationOptions(meta: ScheduleMeta): number[] {
  const maxHours = Math.floor(
    (toMinutes(meta.workingHours.end) - toMinutes(meta.workingHours.start)) / 60
  );
  return Array.from({ length: maxHours }, (_, i) => i + 1);
}
